import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  apiErrorMessage,
  loadAdminUsers,
  normalizeResponseData
} from "../../core/api/adminApi";
import { useAuth } from "../../core/auth/AuthContext";
import type { MigrationUserListItem } from "../../shared/types/admin";
import { resolveAvatarSrc } from "../../shared/ui/avatar";

const DEFAULT_LIMIT = 50;

function resolveUserId(user: MigrationUserListItem): string {
  return user.userId || user.uid || "";
}

function formatDate(value?: string): string {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString("de-DE");
}

function Badge({ label, ok }: { label: string; ok?: boolean }) {
  return <span className={`badge ${ok ? "ok" : ""}`}>{label}</span>;
}

export function UserAdminPage() {
  const { token } = useAuth();
  const navigate = useNavigate();

  const [query, setQuery] = useState("");
  const [limit, setLimit] = useState(DEFAULT_LIMIT);
  const [activeDays, setActiveDays] = useState<string>("");
  const [inactiveDays, setInactiveDays] = useState<string>("");
  const [sort, setSort] = useState("active");
  const [onlyIncomplete, setOnlyIncomplete] = useState(false);
  const [onlyNoFriends, setOnlyNoFriends] = useState(false);
  const [onlyNoLists, setOnlyNoLists] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [users, setUsers] = useState<MigrationUserListItem[]>([]);

  const loadUsers = async () => {
    setLoading(true);
    setError("");
    try {
      const response = await loadAdminUsers(token, {
        query: query || undefined,
        limit,
        activeDays: activeDays ? Number(activeDays) : undefined,
        inactiveDays: inactiveDays ? Number(inactiveDays) : undefined,
        sort,
        onlyIncomplete,
        onlyNoFriends,
        onlyNoLists
      });
      const payload = normalizeResponseData(response);
      setUsers(payload?.users ?? []);
    } catch (e) {
      setError(`User load failed: ${apiErrorMessage(e)}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadUsers();
  }, []);

  const openUser = (user: MigrationUserListItem) => {
    const userId = resolveUserId(user);
    if (!userId) return;
    navigate(`/users/${encodeURIComponent(userId)}`, { state: { user } });
  };

  return (
    <section>
      <div className="view-headline">
        <h1>User Administration</h1>
        <p className="muted">Postgres-User verwalten und prüfen.</p>
      </div>

      <div className="card">
        <div className="toolbar-grid cleanup-grid">
          <div>
            <label>Suche</label>
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Name, E-Mail, City, UID"
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  event.preventDefault();
                  void loadUsers();
                }
              }}
            />
          </div>
          <div>
            <label>Limit</label>
            <select value={limit} onChange={(event) => setLimit(Number(event.target.value))}>
              {[10, 20, 50, 100, 200].map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label>Aktiv in letzten Tagen</label>
            <input
              type="number"
              min={0}
              value={activeDays}
              onChange={(event) => setActiveDays(event.target.value)}
              placeholder="z.B. 30"
            />
          </div>
          <div>
            <label>Inaktiv seit Tagen</label>
            <input
              type="number"
              min={0}
              value={inactiveDays}
              onChange={(event) => setInactiveDays(event.target.value)}
              placeholder="z.B. 365"
            />
          </div>
          <div>
            <label>Sortierung</label>
            <select value={sort} onChange={(event) => setSort(event.target.value)}>
              <option value="active">Aktiv</option>
              <option value="updated">Updated</option>
              <option value="login">Login</option>
              <option value="name">Name</option>
            </select>
          </div>
          <div className="checkbox-row">
            <label>Nur unvollständig</label>
            <input
              type="checkbox"
              checked={onlyIncomplete}
              onChange={(event) => setOnlyIncomplete(event.target.checked)}
            />
          </div>
          <div className="checkbox-row">
            <label>Nur ohne Freunde</label>
            <input
              type="checkbox"
              checked={onlyNoFriends}
              onChange={(event) => setOnlyNoFriends(event.target.checked)}
            />
          </div>
          <div className="checkbox-row">
            <label>Nur ohne Listen</label>
            <input
              type="checkbox"
              checked={onlyNoLists}
              onChange={(event) => setOnlyNoLists(event.target.checked)}
            />
          </div>
          <div className="toolbar-button-cell">
            <button type="button" onClick={() => void loadUsers()} disabled={loading}>
              {loading ? "Laden..." : "Laden"}
            </button>
          </div>
        </div>

        <div className="list-meta-row">
          <span className="muted">{users.length} Users geladen.</span>
        </div>

        {error ? <div className="error-box">{error}</div> : null}

        <div className="table-wrap table-wrap-flow">
          <table>
            <thead>
              <tr>
                <th>Avatar</th>
                <th>Name</th>
                <th>E-Mail</th>
                <th>City</th>
                <th>Active</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => {
                const uid = resolveUserId(user);
                const avatarSrc = resolveAvatarSrc(uid, user.avatar);
                return (
                  <tr key={uid || `${user.email}-${user.name}`}>
                    <td>
                      {avatarSrc ? (
                        <img src={avatarSrc} alt="avatar" className="avatar" />
                      ) : (
                        <div className="avatar placeholder" />
                      )}
                    </td>
                    <td>
                      <button className="link-button" onClick={() => openUser(user)} type="button">
                        {user.name || "(ohne Name)"}
                      </button>
                    </td>
                    <td>{user.email || "-"}</td>
                    <td>{user.city || "-"}</td>
                    <td>
                      <div className="muted">updated: {formatDate(user.updatedAt)}</div>
                      <div className="muted">login: {formatDate(user.loginAt)}</div>
                      <div className="muted">active: {formatDate(user.activeAt)}</div>
                    </td>
                    <td>
                      <Badge label={user.onboardingComplete ? "Onboarding" : "Incomplete"} ok={!!user.onboardingComplete} />
                      <Badge label="DB User" ok={!!user.dbUserExists} />
                      <Badge label={`DB WCO ${user.dbWcoCount ?? 0}`} ok={(user.dbWcoCount ?? 0) > 0} />
                      <Badge label={`DB Bucket ${user.dbBucketCount ?? 0}`} ok={(user.dbBucketCount ?? 0) > 0} />
                      <Badge label={`DB Friends ${(user.dbFollowersCount ?? 0) + (user.dbFollowingCount ?? 0)}`} ok={((user.dbFollowersCount ?? 0) + (user.dbFollowingCount ?? 0)) > 0} />
                      <Badge label={`Flags ${user.dbFlagsCount ?? 0}`} ok={(user.dbFlagsCount ?? 0) > 0} />
                    </td>
                  </tr>
                );
              })}
              {users.length === 0 ? (
                <tr>
                  <td colSpan={6} className="muted">
                    Keine User gefunden.
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}
